import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Form, Input, Modal, TreeSelect } from 'antd'
import { BaseOptionType } from 'antd/es/cascader'
import { ModalProps } from 'antd/lib'

import {
  useInstitutionServiceCreateInstitutionEndpoint,
  useInstitutionServiceGetInstitutionHierarchyEndpoint,
  useInstitutionServiceGetInstitutionHierarchyEndpointKey
} from 'src/api/queries'
import { ApiError, GetInstitutionHierarchyResponse } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message, ModalFooterProps, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  onCancel: () => void
} & ModalProps

export type TreeSelectProps = BaseOptionType & GetInstitutionHierarchyResponse

export const normalizeData = (
  node: TreeSelectProps,
  level: number = 1
): TreeSelectProps => ({
  label: node?.name,
  value: node?.id,
  children:
    node?.children?.map((child) => normalizeData(child, level + 1)) || [],
  disabled: level >= 4
})

const Footer = ({ onCancel, onSubmit }: ModalFooterProps) => {
  const { t } = useLocales()

  return (
    <Flex align="center" justify="center" gap={8}>
      <Button onClick={onCancel}>{t('common.cancel')}</Button>
      <SaveConfirm title={`${t('common.add')}?`} onConfirm={onSubmit}>
        <Button type="primary">{t('common.add')}</Button>
      </SaveConfirm>
    </Flex>
  )
}

export const AddInstitutionModal = ({ onCancel }: Props) => {
  const { t } = useLocales()

  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { data } = useInstitutionServiceGetInstitutionHierarchyEndpoint({})

  const { mutate } = useInstitutionServiceCreateInstitutionEndpoint({
    onSuccess: () => {
      message.success(t('organizational_structure.add_success'))
      queryClient.invalidateQueries({
        queryKey: [useInstitutionServiceGetInstitutionHierarchyEndpointKey]
      })
      onCancel()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      requestBody: {
        name: values?.name,
        parentId: values.parentId
      }
    })
  }

  const normalizedData = [normalizeData(data as TreeSelectProps, 1)]

  return (
    <Modal
      maskClosable={false}
      footer={Footer({
        onCancel,
        onSubmit: () => {
          void onSubmit()
        }
      })}
      title={t('organizational_structure.add_button')}
      open
      onCancel={onCancel}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Flex vertical gap={16}>
          <Form.Item
            label={t('organizational_structure.name')}
            name="name"
            rules={requiredRules}
          >
            <Input placeholder={t('organizational_structure.name')} />
          </Form.Item>
          <Form.Item label={t('form.medical_institution')} name="parentId">
            <TreeSelect
              treeData={data ? normalizedData : []}
              placeholder={t('form.medical_institution')}
              onChange={(value) => form.setFieldValue('parentId', value)}
              treeDefaultExpandAll
            />
          </Form.Item>
        </Flex>
      </Form>
      <Divider />
    </Modal>
  )
}
